// The Brain (HELM-UI-BRAIN) — types for memory_map / memory_detail and the pure view math behind the map (zoom about
// a point, pan, fit), kept out of the component so it is unit-tested.
import { callFn } from './api';
import { AGENT_MEMORY_WEB_BASE } from './config';

export interface MemoryNode {
  key: string; id: string; slug: string; title: string; hook?: string | null; type: string; project?: string | null;
  tags: string[]; visibility: string; app?: string | null; updatedAt?: string | null; x: number; y: number; clusterId: string;
}
export interface MemoryCluster { id: string; size: number; x: number; y: number; label: string; projects: string[] }
export interface MemoryMapMeta {
  source: string; method: 'pca' | 'single' | 'degenerate' | 'empty'; dims?: number; explainedVariance?: [number, number];
  total: number; embedded: number; withoutEmbedding: number; skipped?: number; returned: number; limit: number;
  truncated: boolean; projects: string[] | null; generatedAt?: string; cached: boolean;
}
export interface MemoryProjectOption {
  project: string; memories: number; embedded: number; helmProject: { id: string; slug: string; name: string } | null;
}
export interface MemoryMapResponse {
  nodes: MemoryNode[]; clusters: MemoryCluster[]; meta: MemoryMapMeta;
  scope: { project: { id: string; slug: string; name: string } | null; memoryProjects: string[] | null };
  projects: MemoryProjectOption[];
}

/** The SVG works in a fixed square viewBox; node coordinates in [-1, 1] are scaled by WORLD_R. */
export const VIEW = 1000;
export const WORLD_R = 440;
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 16;

export interface ViewState { k: number; tx: number; ty: number }
export const HOME: ViewState = { k: 1, tx: 0, ty: 0 };

export const clampZoom = (k: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, k));

/** Zoom by `factor` keeping the viewBox point (px, py) fixed on screen. */
export function zoomAt(v: ViewState, factor: number, px = 0, py = 0): ViewState {
  const k = clampZoom(v.k * factor);
  const r = k / v.k;
  return { k, tx: px - (px - v.tx) * r, ty: py - (py - v.ty) * r };
}

export const panBy = (v: ViewState, dx: number, dy: number): ViewState => ({ ...v, tx: v.tx + dx, ty: v.ty + dy });

/** Centre the view on a world point at (at least) zoom k. */
export function centreOn(v: ViewState, wx: number, wy: number, k = Math.max(v.k, 3)): ViewState {
  const kk = clampZoom(k);
  return { k: kk, tx: -wx * kk, ty: -wy * kk };
}

export interface Rect { left: number; top: number; width: number; height: number }

/** Size of one viewBox unit in CSS pixels (the square viewBox is letterboxed: xMidYMid meet). */
export const unitPx = (r: Rect): number => Math.min(r.width, r.height) / VIEW || 1;

/** Client (CSS px) → viewBox coordinates, origin at the centre. */
export function clientToView(clientX: number, clientY: number, r: Rect): [number, number] {
  const s = unitPx(r);
  const ox = r.left + (r.width - VIEW * s) / 2;
  const oy = r.top + (r.height - VIEW * s) / 2;
  return [(clientX - ox) / s - VIEW / 2, (clientY - oy) / s - VIEW / 2];
}

/** Node → world coordinates. */
export const world = (n: { x: number; y: number }): [number, number] => [n.x * WORLD_R, n.y * WORLD_R];

/** Categorical slots (dark-surface steps, validated against Helm's #1c1e25), assigned in cluster order — never cycled.
 *  Past eight clusters (the server caps at eight) everything folds to the neutral "other". */
export const CLUSTER_COLORS = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];
export const OTHER_COLOR = '#9094a3';
export function clusterColor(clusterId: string): string {
  const i = Number.parseInt(clusterId.replace(/^c/, ''), 10);
  return Number.isInteger(i) && i >= 0 && i < CLUSTER_COLORS.length ? CLUSTER_COLORS[i]! : OTHER_COLOR;
}

/** Filter <select> value ↔ request payload. "h:<id>" = a Helm project (server maps it to its memory tags),
 *  "m:<tag>" = a raw memory project, "" = everything Helm may read. */
export function scopePayload(value: string): Record<string, string> {
  if (value.startsWith('h:')) return { project: value.slice(2) };
  if (value.startsWith('m:')) return { memoryProject: value.slice(2) };
  return {};
}
export function optionValue(o: MemoryProjectOption): string {
  return o.helmProject ? `h:${o.helmProject.id}` : `m:${o.project}`;
}

/** One entry per Helm project (several memory tags can belong to one) + one per unclaimed memory tag. */
export function scopeOptions(list: MemoryProjectOption[]): Array<{ value: string; label: string; memories: number }> {
  const out = new Map<string, { value: string; label: string; memories: number }>();
  for (const o of list) {
    const value = optionValue(o);
    const prev = out.get(value);
    if (prev) { prev.memories += o.memories; continue; }
    out.set(value, { value, label: o.helmProject ? o.helmProject.name : o.project, memories: o.memories });
  }
  return [...out.values()];
}

/** memory_detail → one memory, complete and readable (the gateway's safe DTO; no embedding or owner id). */
export interface MemoryRecord {
  id: string; slug: string; title: string; hook: string | null; body: string; bodyTruncated: boolean; type: string;
  project: string | null; tags: string[]; visibility: string; app: string | null; ownedByYou: boolean;
  source: string | null; createdAt: string | null; updatedAt: string | null; links: string[];
}

/** What the details panel shows for the selected node. */
export type MemoryDetailState =
  | { status: 'loading' }
  | { status: 'ready'; memory: MemoryRecord }
  | { status: 'denied'; message: string }    // 403: Helm lacks agent_memory_read (or the project is not allowed)
  | { status: 'missing' }                    // 404: deleted, archived/superseded or no longer visible to you
  | { status: 'error'; message: string };    // anything else: retryable

export function detailFailure(e: unknown): MemoryDetailState {
  const status = typeof e === 'object' && e !== null && typeof (e as { status?: unknown }).status === 'number' ? (e as { status: number }).status : 0;
  const message = e instanceof Error ? e.message : String(e);
  if (status === 403) return { status: 'denied', message };
  if (status === 404) return { status: 'missing' };
  return { status: 'error', message: message || 'Could not load this memory' };
}

/** Load one memory by the map node's id. Only the id is sent; the gateway knows who is asking. Never throws. */
export async function fetchMemoryDetail(
  id: string,
  call: (action: string, payload: Record<string, unknown>) => Promise<unknown> = callFn,
): Promise<MemoryDetailState> {
  try {
    const r = (await call('memory_detail', { id })) as { memory?: MemoryRecord | null } | null;
    return r?.memory ? { status: 'ready', memory: r.memory } : { status: 'missing' };
  } catch (e) {
    return detailFailure(e);
  }
}

/** agent_memory's own page for a memory (its web app's /memory/:slug route), or null when that app isn't configured. */
export function memoryWebUrl(slug: string, base: string = AGENT_MEMORY_WEB_BASE): string | null {
  return base && slug ? `${base}/memory/${encodeURIComponent(slug)}` : null;
}
